import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/Photos.css';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import {
  enqueueOfflinePhotoUpload,
  listOfflinePhotoQueue,
  processOfflinePhotoQueue,
  subscribeOfflinePhotoQueue,
} from '../utils/offlinePhotoQueue';

const THUMBNAIL_FALLBACK =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f5f5f5" width="100" height="100"/%3E%3Ctext x="50" y="50" font-size="12" fill="%23999" text-anchor="middle" dominant-baseline="middle"%3EG%C3%B6rsel%3C/text%3E%3C/svg%3E';
const LIGHTBOX_FALLBACK =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23f5f5f5" width="800" height="600"/%3E%3Ctext x="400" y="300" font-size="24" fill="%23999" text-anchor="middle" dominant-baseline="middle"%3EG%C3%B6rsel%20Bulunamad%C4%B1%3C/text%3E%3C/svg%3E';

const normalizePhotoPath = (value) => String(value || '').trim().replace(/\\/g, '/');

const buildPhotoFileUrl = (filePath) => {
  const normalized = normalizePhotoPath(filePath);
  if (!normalized) return '';
  if (/^https?:\/\//i.test(normalized)) return normalized;

  const origin = API_BASE_URL.replace(/\/api\/?$/, '');
  if (normalized.startsWith('/')) return `${origin}${normalized}`;
  return `${origin}/${normalized}`;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const Photos = () => {
  const [photos, setPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({});
  const [offlinePhotos, setOfflinePhotos] = useState([]);
  const [syncNotice, setSyncNotice] = useState('');
  const [formData, setFormData] = useState({
    description: '',
    project_id: '',
    task_id: '',
    issue_note: '',
    shot_date: new Date().toISOString().split('T')[0],
    file: null,
  });

  const resolvePhotoImageSrc = useCallback(
    (photo) => {
      if (!photo) return '';
      if (photoUrls[photo.id]) return photoUrls[photo.id];
      return buildPhotoFileUrl(photo.file_path);
    },
    [photoUrls]
  );

  const fetchPhotoDataUrl = useCallback(async (photoId) => {
    if (!photoId) return '';
    try {
      const response = await apiClient.get(`${API_BASE_URL}/photos/${photoId}/download`, {
        responseType: 'blob',
      });
      const dataUrl = await blobToDataUrl(response.data);
      if (dataUrl) {
        setPhotoUrls((prev) => ({ ...prev, [photoId]: dataUrl }));
      }
      return dataUrl || '';
    } catch (error) {
      return '';
    }
  }, []);

  const filteredTasks = useMemo(() => {
    if (!formData.project_id) return tasks;
    return tasks.filter((task) => Number(task.project_id) === Number(formData.project_id));
  }, [formData.project_id, tasks]);

  const refreshOfflinePhotos = useCallback(async () => {
    try {
      const queue = await listOfflinePhotoQueue();
      setOfflinePhotos(queue);
    } catch (error) {
      setOfflinePhotos([]);
    }
  }, []);

  useEffect(() => {
    let nextPhotos = photos;

    if (projectFilter !== 'all') {
      nextPhotos = nextPhotos.filter((photo) => photo.project_id && String(photo.project_id) === projectFilter);
    }

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      nextPhotos = nextPhotos.filter((photo) => {
        const description = String(photo.description || '').toLowerCase();
        const issueNote = String(photo.issue_note || '').toLowerCase();
        const taskTitle = String(photo.task_title || '').toLowerCase();
        return description.includes(query) || issueNote.includes(query) || taskTitle.includes(query);
      });
    }

    setFilteredPhotos(nextPhotos);
  }, [photos, searchTerm, projectFilter]);

  useEffect(() => {
    let isMounted = true;

    const loadPhotoUrls = async () => {
      try {
        const pairs = await Promise.allSettled(
          photos.map(async (photo) => {
            if (!photo?.id) return null;
            const dataUrl = await fetchPhotoDataUrl(photo.id);
            if (!dataUrl) return null;
            return [photo.id, dataUrl];
          })
        );

        if (!isMounted) return;
        const nextMap = {};
        pairs.forEach((pair) => {
          if (pair.status !== 'fulfilled' || !pair.value) return;
          const [id, dataUrl] = pair.value;
          nextMap[id] = dataUrl;
        });
        setPhotoUrls(nextMap);
      } catch (err) {
        if (isMounted) setPhotoUrls({});
      }
    };

    if (photos.length > 0) {
      loadPhotoUrls();
    } else {
      setPhotoUrls({});
    }

    return () => {
      isMounted = false;
    };
  }, [photos, fetchPhotoDataUrl]);

  const fetchPhotos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${API_BASE_URL}/photos`);
      setPhotos(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      setError('Fotoğraf listesi yüklenemedi.');
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/projects`);
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setProjects([]);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/tasks`);
      setTasks(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setTasks([]);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
    fetchProjects();
    fetchTasks();
  }, [fetchPhotos, fetchProjects, fetchTasks]);

  useEffect(() => {
    refreshOfflinePhotos();
    const unsubscribe = subscribeOfflinePhotoQueue(() => {
      refreshOfflinePhotos();
    });

    const handleOnline = () => {
      refreshOfflinePhotos();
      fetchPhotos();
    };

    window.addEventListener('online', handleOnline);
    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
    };
  }, [fetchPhotos, refreshOfflinePhotos]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, file }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      description: '',
      project_id: '',
      task_id: '',
      issue_note: '',
      shot_date: new Date().toISOString().split('T')[0],
      file: null,
    });
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      setError('Lütfen bir resim dosyası seçiniz.');
      return;
    }

    if (!formData.project_id) {
      setError('Lütfen bir proje seçiniz.');
      return;
    }

    try {
      const payloadFields = {
        description: formData.description,
        project_id: formData.project_id,
        task_id: formData.task_id || '',
        issue_note: formData.issue_note || '',
        shot_date: formData.shot_date || '',
      };

      if (!window.navigator.onLine) {
        await enqueueOfflinePhotoUpload({
          url: `${API_BASE_URL}/photos`,
          fields: payloadFields,
          file: formData.file,
        });
        resetForm();
        setShowUploadModal(false);
        setSyncNotice('Bağlantı yok. Fotoğraf kuyruğa alındı ve internet geldiğinde yüklenecek.');
        setError('');
        refreshOfflinePhotos();
        return;
      }

      const uploadFormData = new FormData();
      uploadFormData.append('photo', formData.file);
      Object.entries(payloadFields).forEach(([key, value]) => {
        uploadFormData.append(key, value);
      });

      await apiClient.post(`${API_BASE_URL}/photos`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      resetForm();
      setShowUploadModal(false);
      setError('');
      setSyncNotice('Fotoğraf yüklendi.');
      fetchPhotos();
    } catch (err) {
      setError('Fotoğraf yüklenemedi.');
    }
  };

  const handleSyncOfflinePhotos = async () => {
    try {
      const result = await processOfflinePhotoQueue();
      await refreshOfflinePhotos();
      if (result.processed > 0) {
        setSyncNotice(`${result.processed} bekleyen fotoğraf sunucuya gönderildi.`);
        fetchPhotos();
      } else if (result.remaining > 0) {
        setSyncNotice(`${result.remaining} fotoğraf hâlâ kuyrukta bekliyor.`);
      } else {
        setSyncNotice('Bekleyen fotoğraf yok.');
      }
      setError('');
    } catch (err) {
      setError('Bekleyen fotoğraflar senkronize edilirken hata oluştu.');
    }
  };

  const handleDeletePhoto = async (id) => {
    if (!window.confirm('Bu fotoğrafı silmek istediğinizden emin misiniz?')) return;
    try {
      await apiClient.delete(`${API_BASE_URL}/photos/${id}`);
      fetchPhotos();
      setError('');
    } catch (err) {
      setError('Fotoğraf silinirken hata oluştu.');
    }
  };

  const handleViewPhoto = async (photo) => {
    setSelectedPhoto(photo);
    setShowLightbox(true);
    if (!photoUrls[photo?.id]) {
      await fetchPhotoDataUrl(photo?.id);
    }
  };

  const handleCloseLightbox = () => {
    setShowLightbox(false);
    setSelectedPhoto(null);
  };

  return (
    <div className="photos-container">
      <div className="photos-header">
        <div>
          <h1>Fotoğraf Galerisi</h1>
          <p className="photos-subtitle">Sahadan gelen görselleri proje, görev ve problem kaydı ile birlikte yönetin.</p>
        </div>
        <button className="btn-upload-photo" onClick={() => setShowUploadModal(true)} type="button">
          Fotoğraf Yükle
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}
      {syncNotice && <div className="success-alert">{syncNotice}</div>}

      {offlinePhotos.length > 0 && (
        <div className="offline-photo-panel">
          <div className="offline-photo-panel-head">
            <div>
              <h3>Bekleyen Fotoğraf Yüklemeleri</h3>
              <p>Bu kayıtlar cihazda tutuluyor. Bağlantı geldiğinde otomatik veya manuel senkronize edilir.</p>
            </div>
            <button type="button" className="btn-upload-photo" onClick={handleSyncOfflinePhotos}>
              Şimdi Senkronize Et
            </button>
          </div>
          <div className="offline-photo-grid">
            {offlinePhotos.map((item) => (
              <div key={item.id} className="offline-photo-card">
                <div className="offline-photo-preview">
                  <img src={item.file_data_url || THUMBNAIL_FALLBACK} alt={item.fields?.description || item.file_name} />
                </div>
                <div className="offline-photo-meta">
                  <strong>{item.fields?.description || item.file_name || 'Bekleyen fotoğraf'}</strong>
                  <span>
                    Proje: {projects.find((project) => Number(project.id) === Number(item.fields?.project_id))?.name || item.fields?.project_id || '-'}
                  </span>
                  <span>
                    Görev: {tasks.find((task) => Number(task.id) === Number(item.fields?.task_id))?.title || '-'}
                  </span>
                  <span>Çekim: {item.fields?.shot_date || '-'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {photos.length > 0 && (
        <div className="photo-stats">
          <div className="stat-card">
            <h3>Toplam</h3>
            <p>{photos.length}</p>
          </div>
          <div className="stat-card">
            <h3>Filtrelenen</h3>
            <p>{filteredPhotos.length}</p>
          </div>
          <div className="stat-card">
            <h3>Projeler</h3>
            <p>{new Set(photos.map((photo) => photo.project_id)).size}</p>
          </div>
        </div>
      )}

      {photos.length > 0 && (
        <div className="photos-controls">
          <input
            type="text"
            className="search-input"
            placeholder="Açıklama, görev veya problem ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select className="project-filter-select" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
            <option value="all">Tüm projeler</option>
            {projects.map((project) => (
              <option key={project.id} value={String(project.id)}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="loading">Fotoğraflar yükleniyor...</div>
      ) : filteredPhotos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">IMG</div>
          <p className="empty-text">
            {photos.length === 0
              ? 'Henüz fotoğraf yok. İlk fotoğrafı yükleyerek başlayın.'
              : 'Arama kriterlerine uygun fotoğraf bulunamadı.'}
          </p>
          <button className="empty-action" onClick={() => setShowUploadModal(true)} type="button">
            Fotoğraf Yükle
          </button>
        </div>
      ) : (
        <div className="photos-grid">
          {filteredPhotos.map((photo) => (
            <div key={photo.id} className="photo-item">
              <div className="photo-image-container">
                {resolvePhotoImageSrc(photo) ? (
                  <>
                    <img
                      src={resolvePhotoImageSrc(photo)}
                      alt={photo.description || `Fotoğraf ${photo.id}`}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = THUMBNAIL_FALLBACK;
                      }}
                    />
                    <div className="photo-overlay">
                      <button className="btn-view-photo" onClick={() => handleViewPhoto(photo)} title="Görüntüle" type="button">
                        Gör
                      </button>
                      <button className="btn-delete-photo" onClick={() => handleDeletePhoto(photo.id)} title="Sil" type="button">
                        Sil
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="photo-placeholder">IMG</div>
                )}
              </div>
              <div className="photo-info">
                <p className="photo-title" title={photo.description || `Fotoğraf #${photo.id}`}>
                  {photo.description || `Fotoğraf #${photo.id}`}
                </p>
                <div className="photo-meta-list">
                  <p className="photo-date">Çekim: {photo.shot_date ? new Date(photo.shot_date).toLocaleDateString('tr-TR') : '-'}</p>
                  <p className="photo-date">Görev: {photo.task_title || '-'}</p>
                  <p className="photo-date">Problem: {photo.issue_note || '-'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Fotoğraf Yükle</h2>
              <button className="btn-close" onClick={() => setShowUploadModal(false)} type="button">
                ×
              </button>
            </div>

            <form onSubmit={handleUploadSubmit}>
              <div className="form-group">
                <label className="form-label">Fotoğraf Dosyası *</label>
                <label className="file-upload">
                  <div className="file-upload-icon">FILE</div>
                  <p className="file-upload-text">{formData.file ? formData.file.name : 'Dosya seçmek için tıklayın'}</p>
                  <input type="file" accept="image/*" onChange={handleFileChange} />
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">Proje *</label>
                <select className="form-select" name="project_id" value={formData.project_id} onChange={handleInputChange} required>
                  <option value="">Proje seçiniz</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">İlgili Görev</label>
                <select className="form-select" name="task_id" value={formData.task_id} onChange={handleInputChange}>
                  <option value="">Görev seçiniz (opsiyonel)</option>
                  {filteredTasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Çekim Tarihi</label>
                <input type="date" className="form-input" name="shot_date" value={formData.shot_date} onChange={handleInputChange} />
              </div>

              <div className="form-group">
                <label className="form-label">İlgili Problem / Risk</label>
                <input
                  type="text"
                  className="form-input"
                  name="issue_note"
                  value={formData.issue_note}
                  onChange={handleInputChange}
                  placeholder="Örn: Zemin çatlağı, kablo hasarı"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Açıklama</label>
                <textarea
                  className="form-textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Fotoğraf açıklaması"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  Yükle
                </button>
                <button type="button" className="btn-cancel" onClick={() => setShowUploadModal(false)}>
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showLightbox && selectedPhoto && (
        <div className="lightbox-overlay" onClick={handleCloseLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={handleCloseLightbox} type="button">
              ×
            </button>
            {resolvePhotoImageSrc(selectedPhoto) ? (
              <img
                src={resolvePhotoImageSrc(selectedPhoto)}
                alt={selectedPhoto.description || `Fotoğraf ${selectedPhoto.id}`}
                className="lightbox-image"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = LIGHTBOX_FALLBACK;
                }}
              />
            ) : (
              <img src={LIGHTBOX_FALLBACK} alt="Görsel bulunamadı" className="lightbox-image" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Photos;
